// Address assembly. The API returns grouped kanji/romaji fields and NO pre-built
// address string — we build the display strings here.
//
//   kanji  : prefecture+city+ward+town+block+detail  (largest -> smallest, no
//            separators)            e.g. 東京都台東区東上野
//   romaji : [block town], detail, ward, city, prefecture  (smallest -> largest,
//            comma-separated) + " <postal> Japan"
//            e.g. Higashiueno, Taito-Ku, Tokyo 110-0015 Japan
//
// Empty fields are skipped — city/ward are legitimately "" for Tokyo's 23
// special wards.

const PLACEHOLDER_JA = '‹番地・建物名›';
const PLACEHOLDER_EN = '‹street / building›';

export { PLACEHOLDER_JA, PLACEHOLDER_EN };

// "1100015" -> "110-0015". Leaves anything not 7 digits untouched.
export function formatPostal(code) {
  const c = String(code || '').replace(/[-\s]/g, '');
  return /^\d{7}$/.test(c) ? `${c.slice(0, 3)}-${c.slice(3)}` : c;
}

// For 大口事業所 (business) entries the establishment name is part of the complete
// address: appended after the address in Japanese, placed first in English.

// Japanese address without the postal code: 東京都台東区東上野 株式会社 …
export function japaneseBase(entry) {
  const k = entry.kanji || {};
  const address = [k.prefecture, k.city, k.ward, k.town, k.block, k.detail]
    .filter(Boolean)
    .join('');
  const company = entry.business && entry.business.kanji;
  return company ? `${address} ${company}` : address;
}

// English address without the postal code: [Company, ]Higashiueno, Taito-Ku, Tokyo
export function englishBase(entry) {
  const r = entry.romaji || {};
  const street = [r.block, r.town].filter(Boolean).join(' ');
  const address = [street, r.detail, r.ward, r.city, r.prefecture]
    .filter(Boolean)
    .join(', ');
  const company = entry.business && entry.business.romaji;
  return company ? `${company}, ${address}` : address;
}

// Full Japanese line: 〒110-0015 東京都台東区東上野
export function japaneseFull(entry, postalCode) {
  return `〒${formatPostal(postalCode)} ${japaneseBase(entry)}`;
}

// Full English line: Higashiueno, Taito-Ku, Tokyo 110-0015 Japan
export function englishFull(entry, postalCode) {
  return `${englishBase(entry)} ${formatPostal(postalCode)} Japan`;
}

// Page-1 assembled strings, with the user's supplement (番地・建物名 / street).
// The Japanese supplement appends to the end, the English one goes to the front.
// When the supplement is empty a placeholder is shown.
export function japaneseAssembled(entry, postalCode, supplement) {
  const supp = (supplement || '').trim() || PLACEHOLDER_JA;
  return `${japaneseFull(entry, postalCode)} ${supp}`;
}

export function englishAssembled(entry, postalCode, supplement) {
  const supp = (supplement || '').trim() || PLACEHOLDER_EN;
  return `${supp} ${englishFull(entry, postalCode)}`;
}

// ---- Online-form field mapping --------------------------------------------
// Decompose an address into the fields a checkout / shipping form asks for, so
// users know which value goes where. `banchi` and `building` are the user's
// supplement (street number / building·room), woven into the right field.
//
// City rule (the non-obvious part):
//   - both city & ward present (ordinance cities, 郡 towns) -> "Ward, City"
//   - ward only (Tokyo's 23 special wards, city == "")       -> ward
//   - city only                                              -> city

// Western / EC checkout form (romaji values).
export function ecFormFields(entry, postalCode, banchi = '', building = '') {
  const r = entry.romaji || {};
  const city = r.city && r.ward ? `${r.ward}, ${r.city}` : r.ward || r.city;
  const line1 = [banchi.trim(), r.block, r.town].filter(Boolean).join(' ');
  const line2 = [building.trim(), r.detail].filter(Boolean).join(', ');
  return [
    { key: 'country', label: 'Country / Region', value: 'Japan' },
    { key: 'zip', label: 'ZIP / Postal code', value: formatPostal(postalCode) },
    { key: 'state', label: 'State / Prefecture', value: r.prefecture },
    { key: 'city', label: 'City', value: city },
    { key: 'addr1', label: 'Address Line 1', value: line1 },
    { key: 'addr2', label: 'Address Line 2', value: line2 },
  ];
}

// Japanese form (kanji values). Japanese fields concatenate (no separators).
export function jaFormFields(entry, postalCode, banchi = '', building = '') {
  const k = entry.kanji || {};
  return [
    { key: 'zip', label: '郵便番号', value: formatPostal(postalCode) },
    { key: 'pref', label: '都道府県', value: k.prefecture },
    { key: 'city', label: '市区町村', value: `${k.city}${k.ward}` },
    { key: 'machi', label: '町名・番地', value: `${k.town}${k.block}${banchi.trim()}` },
    { key: 'tatemono', label: '建物名・部屋番号', value: `${building.trim()}${k.detail}` },
  ];
}
